import React, { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import dayjs from 'utils/extendedDayJS';
import { Button } from '@epoch-ui/components/Button';
import { Form, FormFieldSet, CheckboxField, DateTimePickerField } from '@epoch-ui/components/Form';
import { Modal, ModalContent, ModalHeader, ModalTitle } from '@epoch-ui/components/Modal/modal';
import { RadioField, RadioFieldItem } from '@epoch-ui/components/Form/fields/RadioField/RadioField';
import { convertSendAtToFormValue } from 'pages/dashboard/overview/events/schemas/commonUtils';
import { useToaster, ToasterVariant } from 'hooks/useToaster';
import { useEditableEvent } from '@event-editor/hooks/useEditableEvent';
import { useResendFeedbackFormModal } from '@event-editor/hooks/eventEditorState';
import { eventEditorQueryKeys } from '@event-editor/queryKeys';
import { resendFeedbackRequestApi } from '@event-editor/api/feedback/resendFeedbackRequest';

import { COMMUNICATION_REMINDER_MEDIUM } from '../../EditEventCommunications/types/communication.types';

import {
  resendFeedbackFormSchema,
  resendFeedbackFormInput,
  FEEDBACK_REQUEST_TYPE,
  getResendFeedbackDefaults,
  resendFeedbackFormValues
} from './resendFeedbackFormSchema';

export const ResendFeedbackFormModal = (): React.ReactElement => {
  const event = useEditableEvent();
  const queryClient = useQueryClient();
  const { showToast } = useToaster();
  const { isOpen, onSuccess, feedbackRequest, close } = useResendFeedbackFormModal();

  // Convert feedbackRequest to form values
  const initialValues = useMemo((): resendFeedbackFormInput => {
    if (!feedbackRequest) {
      return getResendFeedbackDefaults(event.timezoneCode);
    }

    return {
      type: FEEDBACK_REQUEST_TYPE.SCHEDULE,
      sendAt: convertSendAtToFormValue(feedbackRequest.sendAt, event.timezoneCode),
      notificationChannel: {
        email: feedbackRequest.mediums.includes(COMMUNICATION_REMINDER_MEDIUM.EMAIL),
        slack: feedbackRequest.mediums.includes(COMMUNICATION_REMINDER_MEDIUM.SLACK)
      }
    };
  }, [feedbackRequest, event.timezoneCode]);

  const createMutation = useMutation({
    mutationFn: resendFeedbackRequestApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: eventEditorQueryKeys.eventCommunication(event.id!)
      });
      onSuccess?.();
      close();
    },
    onError: (error) => {
      console.error(error);
      showToast('Failed to resend feedback request', { variant: ToasterVariant.ERROR });
    }
  });

  const updateMutation = useMutation({
    mutationFn: resendFeedbackRequestApi.update,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: eventEditorQueryKeys.eventCommunication(event.id!)
      });
      onSuccess?.();
      close();
    },
    onError: (error) => {
      console.error(error);
      showToast('Failed to update resend feedback request', { variant: ToasterVariant.ERROR });
    }
  });

  const methods = useForm<resendFeedbackFormInput, unknown, resendFeedbackFormValues>({
    resolver: zodResolver(resendFeedbackFormSchema(event.timezoneCode)),
    values: initialValues
  });

  const {
    handleSubmit,
    watch,
    formState: { isSubmitting }
  } = methods;

  const type = watch('type');

  const isPending = createMutation.isPending || updateMutation.isPending;

  const onSubmit = handleSubmit(
    (data: resendFeedbackFormValues) => {
      if (!event.id) return;

      if (feedbackRequest) {
        updateMutation.mutate({
          requestId: feedbackRequest.id,
          ...data
        });
      } else {
        createMutation.mutate({
          eventId: event.id,
          ...data
        });
      }
    },
    (errors) => {
      console.error(errors);
    }
  );

  return (
    <Modal open={isOpen} onOpenChange={(nextOpen) => (nextOpen ? undefined : close())}>
      <ModalContent
        size="sm"
        className="tw-flex tw-flex-col"
        data-testid="resend-feedback-form-modal"
      >
        <ModalHeader>
          <ModalTitle className="tw-text-dark-black">Resend feedback form</ModalTitle>
          <p className="tw-mb-1 tw-mt-2 tw-text-sm tw-text-primary-grey">
            You can send guests a reminder to fill out the feedback form. The reminder will only be
            sent to guests that have not yet responded.
          </p>
        </ModalHeader>

        <Form {...methods}>
          <form onSubmit={onSubmit} className="tw-flex tw-flex-col">
            <FormFieldSet className="tw-gap-4">
              {/* Send feedback form notification */}
              <RadioField
                name="type"
                label="When do you want to send the feedback form notification?"
                className="tw-mt-3"
              >
                <RadioFieldItem
                  value={FEEDBACK_REQUEST_TYPE.NOW}
                  onClick={() =>
                    methods.setValue(
                      'sendAt',
                      convertSendAtToFormValue(dayjs.utc(), event.timezoneCode)
                    )
                  }
                >
                  Now
                </RadioFieldItem>
                <RadioFieldItem
                  value={FEEDBACK_REQUEST_TYPE.SCHEDULE}
                  onClick={() =>
                    methods.setValue(
                      'sendAt',
                      convertSendAtToFormValue(dayjs.utc(), event.timezoneCode)
                    )
                  }
                >
                  Schedule
                </RadioFieldItem>
              </RadioField>

              {type === FEEDBACK_REQUEST_TYPE.SCHEDULE && (
                <DateTimePickerField
                  name="sendAt"
                  label="Send the feedback form reminder:"
                  description={
                    event &&
                    `You're scheduling in (${dayjs.getGMTOffset(
                      event.timezoneCode
                    )}) - ${dayjs.getTimezoneName(event.timezoneCode)}.`
                  }
                >
                  <DateTimePickerField.DateInput placeholder="Date" />
                  <DateTimePickerField.Time interval={15} placeholder="Time" />
                </DateTimePickerField>
              )}

              {/* Notification channels */}
              <div>
                <h3 className="tw-mb-0 tw-text-sm tw-font-medium">
                  How do you want to notify guests?
                </h3>
                <div className="tw-mt-3 tw-flex tw-flex-col tw-gap-2">
                  <CheckboxField
                    className="tw-mt-0"
                    name="notificationChannel.email"
                    label="Email"
                  />
                  <CheckboxField
                    className="tw-mt-0"
                    name="notificationChannel.slack"
                    label="Slack"
                  />
                </div>
              </div>

              <div className="tw-mt-1">
                <Button
                  size="sm"
                  className="tw-w-full"
                  type="submit"
                  disabled={isSubmitting || isPending}
                >
                  Send
                </Button>
              </div>
            </FormFieldSet>
          </form>
        </Form>
      </ModalContent>
    </Modal>
  );
};
